import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Inject, inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { SharedMethodsModule } from 'app/core/shared-services/shared-methods.module';
import { ChartConfiguration } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-view-usage',
  templateUrl: './view-usage.component.html',
  styleUrls: ['./view-usage.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, NgClass, BaseChartDirective]
})
export class ViewUsageComponent implements OnInit {

  public isScreenSmall: boolean;

  public actionKey = '';
  public aggregation = 'get_usage_data_chart';
  public loading: boolean = false;
  public showBundleBtn: boolean = true;
  public bundleUsage: Array<any> = [];
  public view = 'bundle';

  private radiusResponse: any;
  private radiusChartResponseDetails: any;
  private radiusBundleResponseDetails: any;
  public activeBundles: any;

  public barChartLegend = true;
  public barChartPlugins = [];

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  public barChartData: ChartConfiguration<'bar'>['data'] = {
    labels: [],
    datasets: []
  };

  public barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
  };

  /**
     * Constructor
     */
  constructor(
    private _sharedModule: SharedMethodsModule,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ){}

  // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
  async ngOnInit() {
    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
    .pipe(takeUntil(this._unsubscribeAll))
    .subscribe(({matchingAliases}) =>
    {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
    });

    this.loading = true;
    this.radiusResponse = await this._sharedModule.getCustomerRadiusUsage(this.data.service.ServiceAccountEntityID);
    if(this.radiusResponse.success) {
      this.radiusChartResponseDetails = await this._sharedModule.getCustomerRadiusActions(this.data.service.ServiceAccountEntityID, 'get_usage_data_chart', 'day' );

      if(this.radiusChartResponseDetails?.result?.ClientSideInstructionData) {

        // push chart label
        this.radiusChartResponseDetails.result.ClientSideInstructionData.Labels.forEach(element => {
          this.barChartData.labels.push(element)
        });

        // push chart series
        const upload = this.radiusChartResponseDetails.result.ClientSideInstructionData.Series.indexOf('Input (MB)'); 
        const download = this.radiusChartResponseDetails.result.ClientSideInstructionData.Series.indexOf('Output (MB)');

        for (let index = 0; index < this.radiusChartResponseDetails.result.ClientSideInstructionData.Datasets.length; index++) {
          if(index == upload) {
            let dataSetsToGB = [];
            for (let idx = 0; idx < this.radiusChartResponseDetails.result.ClientSideInstructionData.Datasets[index].length; idx++) {
              dataSetsToGB.push((this.radiusChartResponseDetails.result.ClientSideInstructionData.Datasets[index][idx] / 1024).toFixed(2))
            }
            const upload = {
              data: dataSetsToGB,
              label: 'Upload (GB)',
              backgroundColor: '#FFCB1D'
            }

            this.barChartData.datasets.push(upload)
          } else if(index == download) {
            let dataSetsToGB = [];
            for (let idx = 0; idx < this.radiusChartResponseDetails.result.ClientSideInstructionData.Datasets[index].length; idx++) {
              dataSetsToGB.push((this.radiusChartResponseDetails.result.ClientSideInstructionData.Datasets[index][idx] / 1024).toFixed(2))
            }            
            const download = {
              data: dataSetsToGB,
              label: 'Download (GB)',
              backgroundColor: '#423473'
            }
            this.barChartData.datasets.push(download)
          }
          
        }
      }
      this.radiusBundleResponseDetails = await this._sharedModule.getCustomerRadiusActions(this.data.service.ServiceAccountEntityID, 'get_active_bundles');
      if(this.radiusBundleResponseDetails.result.ClientSideInstructionData?.payload.length) {
        this.activeBundles = this.radiusBundleResponseDetails.result.ClientSideInstructionData?.payload;
        
        const anyTimeBundles = this.activeBundles.filter(x => x.start_hour === '04:00' && x.end_hour == '22:00');
        const nightTimeBundles = this.activeBundles.filter(x => x.start_hour === '22:00' && x.end_hour === '04:00');
        const allDayBundles = this.activeBundles.filter(x => x.start_hour === '00:00' && x.end_hour === '23:59');

        // the active bundle is the one with the greatest expiry date
        // this sorts by expire date and selects the last item in the array
        const anyTimeActiveBundle = anyTimeBundles.sort((a, b) => new Date(a.expire).getTime() - new Date(b.expire).getTime()).slice(-1)[0];
        const nightTimeActiveBundle = nightTimeBundles.sort((a, b) => new Date(a.expire).getTime() - new Date(b.expire).getTime()).slice(-1)[0];
        const allDayActiveBundle = allDayBundles.sort((a, b) => new Date(a.expire).getTime() - new Date(b.expire).getTime()).slice(-1)[0];

        // the rolled over amount is the left_over amount of the rolled over bundle (the one that does not have the latest expiry date)
        const anyTimeRolledOverBundle = anyTimeBundles.filter(x => x.expire !== anyTimeActiveBundle.expire);
        const nightTimeRollOverBundle = nightTimeBundles.filter(x => x.expire !== nightTimeActiveBundle.expire);
        const allDayRollOverBundle = allDayBundles.filter(x => x.expire !== allDayActiveBundle.expire);

        if (anyTimeBundles.length > 0) {
          // Anytime bundle usage
          this.bundleUsage.push(
            {
              description: 'Anytime Data',
              volume: anyTimeActiveBundle.volume_gb, // the one with the later expiry date is the active bundle
              monthlyUsage: anyTimeBundles.length > 1 ? (anyTimeBundles.reduce((total, bundle) => total + parseFloat(bundle.used_gb), 0)).toFixed(2) : parseFloat(anyTimeBundles[0].used_gb).toFixed(2),
              rolledOver: anyTimeRolledOverBundle.length > 0 ? parseFloat(anyTimeRolledOverBundle[0].left_over_gb).toFixed(2) : 0.00,
              remaining: anyTimeBundles.length > 1 ? (anyTimeBundles.reduce((total, bundle) => total + parseFloat(bundle.left_over_gb), 0)).toFixed(2) : parseFloat(anyTimeBundles[0].left_over_gb).toFixed(2)
            }
          )
        } if (nightTimeBundles.length > 0) {
          // Nighttime bundle usage
          this.bundleUsage.push(
            {
              description: 'Nighttime Data',
              volume: nightTimeActiveBundle.volume_gb, // the one with the later expiry date is the active bundle
              // monthlyUsage: parseFloat(nightTimeActiveBundle.used_gb).toFixed(2),
              monthlyUsage: nightTimeBundles.length > 1 ? (nightTimeBundles.reduce((total, bundle) => total + parseFloat(bundle.used_gb), 0)).toFixed(2) : parseFloat(nightTimeBundles[0].used_gb).toFixed(2),
              rolledOver: nightTimeRollOverBundle.length > 0 ? parseFloat(nightTimeRollOverBundle[0].left_over_gb).toFixed(2) : 0.00,
              remaining: nightTimeBundles.length > 1 ? (nightTimeBundles.reduce((total, bundle) => total + parseFloat(bundle.left_over_gb), 0)).toFixed(2) : parseFloat(nightTimeBundles[0].left_over_gb).toFixed(2)
            }
          )
        } if (allDayBundles.length > 0) {
          // Add-On bundle usage
          this.bundleUsage.push(
            {
              description: 'Add-On',
              volume: allDayActiveBundle.volume_gb, // the one with the later expiry date is the active bundle
              // monthlyUsage: parseFloat(nightTimeActiveBundle.used_gb).toFixed(2),
              monthlyUsage: allDayBundles.length > 1 ? (allDayBundles.reduce((total, bundle) => total + parseFloat(bundle.used_gb), 0)).toFixed(2) : parseFloat(allDayBundles[0].used_gb).toFixed(2),
              rolledOver: allDayRollOverBundle.length > 0 ? parseFloat(allDayRollOverBundle[0].left_over_gb).toFixed(2) : 0.00,
              remaining: allDayBundles.length > 1 ? (allDayBundles.reduce((total, bundle) => total + parseFloat(bundle.left_over_gb), 0)).toFixed(2) : parseFloat(allDayBundles[0].left_over_gb).toFixed(2)
            }
          )
        }
        this.loading = false;

      } else {
        this.view = 'chart';
        this.showBundleBtn = false;
      }
    }
  }
  // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

  public viewType(type: string) {
    this.view = type;
  }

  /**
     * On destroy
     */
  ngOnDestroy(): void
  {
      // Unsubscribe from all subscriptions
      this._unsubscribeAll.next(null);
      this._unsubscribeAll.complete();
  }


}
